import type { LayerKey, WorkspaceKey } from '@festina-life/api'
import type { Optional } from '@festina-life/core'
import { listUtils } from '@festina-life/core'

import type { RoutableConfigurationItem } from '../../types/common'
import type { VersionedConfigurationItemIdentifier } from '../../uiModels/drafts/types'
import type { CreateGlMapDraftInitialValues } from '../createGlMapDraft/types'
import type { CreateParameterDraftInitialValues } from '../createParameterDraft/types'
import type { CreateTemplateDraftInput } from '../createTemplateDraft/types'

export type DraftCreationType = Exclude<
  RoutableConfigurationItem,
  'annulOperation'
>

export const DraftCreationTypes =
  listUtils.arrayOfAllUnique<DraftCreationType>()([
    'template',
    'enumDefinition',
    'parameterDefinition',
    'tenant',
    'glMap',
    'reconciliationSpec',
  ])

export type DraftIdentifier = VersionedConfigurationItemIdentifier & {
  type: DraftCreationType
}

export type DraftCreationInitialValue<T extends DraftCreationType> = Optional<
  T extends 'template'
    ? {
        value: Optional<Partial<CreateTemplateDraftInput>>
        type: 'template'
      }
    : T extends 'enumDefinition'
      ? {
          value: { layer: LayerKey }
          type: 'enumDefinition'
        }
      : T extends 'parameterDefinition'
        ? {
            value: Optional<Partial<CreateParameterDraftInitialValues>>
            type: 'parameterDefinition'
          }
        : T extends 'glMap'
          ? {
              value: Optional<Partial<CreateGlMapDraftInitialValues>>
              type: 'glMap'
            }
          : never
>

export type UntypedDraftCreationInitialValue = {
  type: undefined
  value: Optional<{ packagePath: string[] }>
}

export type DraftCreationModalInitialValue = Optional<
  | DraftCreationInitialValue<DraftCreationType>
  | UntypedDraftCreationInitialValue
>

export type DraftCreatedResult = {
  createdDraft: DraftIdentifier
  addedToWorkspaceKey: WorkspaceKey
}

export type DraftSubmitStatus =
  | { status: 'submitting' }
  | ({ status: 'success' } & DraftCreatedResult)
  | { status: 'error'; error: Error }

export type DraftCreationProps<T extends DraftCreationType> = {
  layer: LayerKey
  block: string
  initialValue?: DraftCreationInitialValue<T>
  onStatusChanged: (status: DraftSubmitStatus) => void
}
